import { Button } from "@gigpro/ui/Button";
import { Dialog, DialogFooter, DialogTitle } from "@gigpro/ui/Dialog";
import { toast } from "@gigpro/ui/Toast";
import type { W2Shift, W2TerminationImpact } from "@sidegigllc/api/client";
import { ApiError } from "@sidegigllc/api/resources";
import { type UseQueryResult, useIsMutating } from "@tanstack/react-query";
import { useUpdateW2EmploymentStatus, useW2TerminationImpact, w2EmploymentStatusMutationKey } from "api/queries/admin";
import { formatInTimeZone } from "date-fns-tz";
import React from "react";

type W2EmploymentAction = "terminate" | "restore";

const copy: Record<
  W2EmploymentAction,
  { title: string; body: string; confirm: string; success: string; status: "terminated" | "onboarded" }
> = {
  terminate: {
    title: "Mark W-2 terminated",
    body: "This sets the pro's employment status in Zeal to terminated and removes their W-2 eligibility. They won't be able to book W-2 shifts.",
    confirm: "Terminate",
    success: "W-2 employment terminated",
    status: "terminated",
  },
  restore: {
    title: "Restore W-2 employment",
    body: "This sets the pro's employment status in Zeal back to live and restores their W-2 eligibility. They'll be able to book W-2 shifts again.",
    confirm: "Restore",
    success: "W-2 employment restored",
    status: "onboarded",
  },
};

const pendingPayLabel: Record<Exclude<W2Shift["reason"], "released">, string> = {
  in_progress: "In progress",
  unpaid: "Unpaid shift",
};

// The API sends no venue timezone, so show the instant in UTC, labelled, rather than in the admin's zone.
const formatShiftStart = (start: Date) => `${formatInTimeZone(start, "UTC", "MMM d, yyyy, h:mm aaa")} (UTC)`;

const HTTP_SERVICE_UNAVAILABLE = 503;

/** `ApiError` drops 5xx bodies, so the 503 message is ours. */
function describeError(error: unknown): string {
  const apiError = ApiError.fromThrown(error);
  if (apiError.httpStatus === HTTP_SERVICE_UNAVAILABLE)
    return "Zeal is unavailable right now. Please try again shortly.";
  return apiError.getMainErrorMessage();
}

function successMessage(action: W2EmploymentAction, impact: W2TerminationImpact) {
  const released = impact.released_shifts.length;
  if (action === "restore" || released === 0) return copy[action].success;
  return `${copy[action].success} · ${released} upcoming ${released === 1 ? "shift" : "shifts"} reopened`;
}

function ShiftList({ label, shifts }: Readonly<{ label: string; shifts: Array<W2Shift> }>) {
  return (
    <div className="flex flex-col gap-1">
      <p className="font-semibold text-wrap">{label}</p>
      <ul aria-label={label} className="flex flex-col gap-1">
        {shifts.map((shift) => (
          <li key={shift.gig_application_id} className="flex flex-wrap justify-between gap-x-2">
            <span>
              {formatShiftStart(shift.start_time)} · {shift.location_name}
            </span>
            {shift.reason !== "released" && <span className="font-semibold">{pendingPayLabel[shift.reason]}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function TerminationImpact({ impact }: Readonly<{ impact: UseQueryResult<W2TerminationImpact, ApiError> }>) {
  if (impact.isPending) return <p className="text-wrap text-gray">Checking this pro's W-2 shifts…</p>;
  if (impact.isError) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-wrap text-error">Couldn't load this pro's W-2 shifts.</p>
        <Button size="small" onClick={() => impact.refetch()} loading={impact.isFetching}>
          Retry
        </Button>
      </div>
    );
  }

  const { released_shifts, pending_pay_shifts } = impact.data;
  return (
    <>
      {!!released_shifts.length && <ShiftList label="Cancelled and reopened for other pros" shifts={released_shifts} />}
      {!!pending_pay_shifts.length && (
        <ShiftList label="Stays assigned and is still paid" shifts={pending_pay_shifts} />
      )}
    </>
  );
}

type W2EmploymentStatusBodyProps = { proId: number; action: W2EmploymentAction; onClose: () => void };

/** Mounted only while the dialog is open, so its state (confirmed action, mutation) resets on close. */
function W2EmploymentStatusBody({ proId, action, onClose }: Readonly<W2EmploymentStatusBodyProps>) {
  // Frozen at open: the refetched status flips `action` while the dialog is still fading out.
  const [confirmedAction] = React.useState(action);
  const { title, body, confirm, status } = copy[confirmedAction];
  const terminating = confirmedAction === "terminate";
  const impact = useW2TerminationImpact(proId, { enabled: terminating });
  // Terminating cancels shifts, so the admin must see the current list (not a cached one mid-refetch) first.
  const previewReady = !terminating || (impact.isSuccess && !impact.isFetching);
  const updateStatus = useUpdateW2EmploymentStatus(proId);

  const onConfirm = () =>
    updateStatus.mutate(status, {
      onSuccess: (result) => {
        toast.success(successMessage(confirmedAction, result));
        onClose();
      },
    });

  return (
    <>
      <DialogTitle>{title}</DialogTitle>
      <p className="text-wrap">{body}</p>
      {terminating && <TerminationImpact impact={impact} />}
      {updateStatus.isError && (
        <p role="alert" className="text-wrap text-error">
          {describeError(updateStatus.error)}
        </p>
      )}
      <DialogFooter>
        <Button onClick={onClose} className="w-full" disabled={updateStatus.isPending}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          className="w-full"
          variant={terminating ? "destructive" : "contained"}
          loading={updateStatus.isPending}
          disabled={updateStatus.isPending || !previewReady}
        >
          {confirm}
        </Button>
      </DialogFooter>
    </>
  );
}

type W2EmploymentStatusDialogProps = {
  open: boolean;
  onClose: () => void;
  proId: number;
  action: W2EmploymentAction;
};

export function W2EmploymentStatusDialog({ open, onClose, proId, action }: Readonly<W2EmploymentStatusDialogProps>) {
  // Escape or a backdrop click mid-request would unmount the body and drop the mutation's success toast.
  const submitting = useIsMutating({ mutationKey: w2EmploymentStatusMutationKey(proId) }) > 0;
  const close = () => {
    if (!submitting) onClose();
  };

  return (
    <Dialog open={open} onClose={close} bottomSheet>
      <W2EmploymentStatusBody proId={proId} action={action} onClose={onClose} />
    </Dialog>
  );
}
